import React, { useState } from 'react';
import styled from 'styled-components';
import { useCart } from '../../context/CartContext';
import { useNavigate } from 'react-router-dom';
import CartPackageItem from './CartPackageItem';
import { usePrices } from '../../context/PriceContext';
// import PayPalButton from '../payment/PayPalButton';
import PromoCodeInput from './PromoCodeInput';
import { analytics } from '../../services/analytics';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const CartContainer = styled.div`
  padding: ${props => props.theme.spacing.large};
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: ${props => props.theme.spacing.medium};
  font-weight: 700;
  color: ${props => props.theme.colors.text.primary};
`;

const EmptyMessage = styled.p`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.text.secondary};
  text-align: center;
  margin-top: ${props => props.theme.spacing.xlarge};
`;

const CartContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xlarge};
`;

const CartItemsList = styled.div`
  flex: 1;
`;

const CartSummary = styled.div`
  background-color: ${props => props.theme.colors.background.tertiary};
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: ${props => props.theme.spacing.large};
  box-shadow: ${props => props.theme.shadows.medium};
  border: 1px solid ${props => props.theme.colors.border.primary};
`;

const SummaryTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: ${props => props.theme.spacing.medium};
  color: ${props => props.theme.colors.text.primary};
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: ${props => props.theme.spacing.medium};
  margin-bottom: ${props => props.theme.spacing.small};
  font-size: 1rem;
  color: ${props => props.theme.colors.text.primary};
`;

const PriceColumn = styled.span`
  text-align: right;
  font-family: monospace;
  min-width: 80px;
  color: ${props => props.theme.colors.text.accent};
`;

const SummaryTotal = styled(SummaryRow)`
  font-weight: 700;
  font-size: 1.2rem;
  margin-top: ${props => props.theme.spacing.medium};
  border-top: 1px solid ${props => props.theme.colors.border.primary};
  padding-top: ${props => props.theme.spacing.medium};
`;

const ProceedToPaymentButton = styled.button`
  width: 100%;
  padding: ${props => props.theme.spacing.medium};
  background-color: #1976d2;
  color: white;
  border: none;
  border-radius: ${props => props.theme.borderRadius.small};
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: ${props => props.theme.spacing.medium};
  transition: all ${props => props.theme.transitions.medium};
  
  &:hover {
    background-color: #1565c0;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(25, 118, 210, 0.3);
  }
  
  &:disabled {
    background-color: ${props => props.theme.colors.background.tertiary};
    color: ${props => props.theme.colors.text.disabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const LoadingIndicator = styled.div`
  text-align: center;
  margin: ${props => props.theme.spacing.xlarge} 0;
  font-size: 1.2rem;
  color: ${props => props.theme.colors.text.secondary};
`;

const PackagePriceInfo = styled.div`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.text.secondary};
  margin-top: ${props => props.theme.spacing.xsmall};
`;


const Cart: React.FC = () => {
  const { user } = useAuth();
  const { cartItems, cartPackages, totalPrice, clearCart, isLoading } = useCart();
  const { 
    priceConfig, 
    getPriceByType, 
    getDiscountByType
  } = usePrices();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState<{ codigo: string; porcentajeDescuento: number } | null>(null);
  const navigate = useNavigate();


  const handleProceedToPayment = async () => {
    if (cartItems.length === 0) return;
    
    // Track checkout initiation
    analytics.beginCheckout(cartItems, finalPrice);
    
    // Si es 100% descuento, procesar directamente
    if (appliedPromo && appliedPromo.porcentajeDescuento === 100) {
      try {
        // 1. Crear compra con monto $0
        const orderData = {
          userId: user?.userId,
          montoTotal: 0,
          descuentoPorcentaje: 100,
          codigoDescuento: appliedPromo.codigo
        };
        
        const response = await axios.post('https://canvasoft.com:8487/canvamanager/market/compra', orderData);
        const { compraId } = response.data;
        
        // 2. Confirmar compra inmediatamente
        await axios.post(`https://canvasoft.com:8487/canvamanager/market/compra/${compraId}/confirmar`, {
          paypalOrderId: 'FREE_ORDER_' + Date.now(),
          paypalStatus: 'COMPLETED'
        });
        
        // 3. Limpiar carrito y ir a success
        clearCart();
        navigate('/payment-success');
        
      } catch (error) {
        console.error('Error procesando orden gratuita:', error);
        // TODO: Mostrar mensaje de error al usuario
      }
    } else {
      // Lógica normal: ir a PayPal
      const params = new URLSearchParams({
        amount: finalPrice.toString(),
        originalAmount: totalPrice.toString()
      });
      
      if (appliedPromo) {
        params.append('promoCode', appliedPromo.codigo);
        params.append('promoDiscount', appliedPromo.porcentajeDescuento.toString());
      }
      
      navigate(`/payment?${params.toString()}`);
    }
  };

  // Manejar aplicación de código promocional
  const handlePromoCodeApplied = (promoData: { codigo: string; porcentajeDescuento: number }) => {
    if (promoData.codigo === '') {
      setAppliedPromo(null);
    } else {
      setAppliedPromo(promoData);
    }
  };

  // Calcular descuento promocional
  const calculatePromoDiscount = () => {
    if (!appliedPromo) return 0;
    return (totalPrice * appliedPromo.porcentajeDescuento) / 100;
  };

  // Calcular precio final con descuento promocional
  const finalPrice = totalPrice - calculatePromoDiscount();


  const renderSummary = () => {
    // Filtrar paquetes por tipo
    const pack10s = cartPackages.filter(pkg => pkg.type === 'pack10');
    const pack5s = cartPackages.filter(pkg => pkg.type === 'pack5');
    const individuals = cartPackages.filter(pkg => pkg.type === 'individual');
    
    // Calcular cantidades
    const pack10Count = pack10s.length;
    const pack5Count = pack5s.length;
    const individualCount = individuals.length;
    
    // Calcular subtotales
    const pack10Total = pack10s.reduce((sum, pkg) => sum + pkg.price, 0);
    const pack5Total = pack5s.reduce((sum, pkg) => sum + pkg.price, 0);
    const individualTotal = individuals.reduce((sum, pkg) => sum + pkg.price, 0);
        
    return (
      <CartSummary>
        <SummaryTitle>Resumen de compra</SummaryTitle>
        {pack10Count > 0 && (
          <SummaryRow>
            <span>
              Paquetes de 10 imágenes ({pack10Count}):
              <PackagePriceInfo>
                (${getPriceByType('pack10')} por imagen • {getDiscountByType('pack10') * 100}% descuento)
              </PackagePriceInfo>
            </span>
            <PriceColumn>${pack10Total.toFixed(2)}</PriceColumn>
          </SummaryRow>
        )}
        {pack5Count > 0 && (
          <SummaryRow>
            <span>
              Paquetes de 5 imágenes ({pack5Count}):
              <PackagePriceInfo>
                (${getPriceByType('pack5')} por imagen • {getDiscountByType('pack5') * 100}% descuento)
              </PackagePriceInfo>
            </span>
            <PriceColumn>${pack5Total.toFixed(2)}</PriceColumn>
          </SummaryRow>
        )}
        {individualCount > 0 && (
          <SummaryRow>
            <span>
              Imágenes individuales ({individualCount}):
              <PackagePriceInfo>
                (${getPriceByType('individual')} por imagen)
              </PackagePriceInfo>
            </span>
            <PriceColumn>${individualTotal.toFixed(2)}</PriceColumn>
          </SummaryRow>
        )}
        <SummaryRow>
          <span>Imágenes en total: {cartItems.length}</span>
          <PriceColumn></PriceColumn>
        </SummaryRow>
        <SummaryRow>
          <span>Subtotal:</span>
          <PriceColumn>${totalPrice.toFixed(2)}</PriceColumn>
        </SummaryRow>
        {appliedPromo && (
          <SummaryRow>
            <span>Descuento ({appliedPromo.codigo} - {appliedPromo.porcentajeDescuento}%):</span>
            <PriceColumn style={{ color: '#4caf50' }}>-${calculatePromoDiscount().toFixed(2)}</PriceColumn>
          </SummaryRow>
        )}
        <SummaryTotal>
          <span>Total (US dollars):</span>
          <PriceColumn>${finalPrice.toFixed(2)}</PriceColumn>
        </SummaryTotal>
        <ProceedToPaymentButton
          onClick={handleProceedToPayment}
          disabled={isProcessing || cartItems.length === 0}
        >
          Procede al pago
        </ProceedToPaymentButton>
        
      </CartSummary>
    );
  };

  // Mostrar indicador de carga si el carrito o los precios están cargando
  if (isLoading || priceConfig.isLoading) {
    return (
      <CartContainer>
        <Title>Mi Carrito</Title>
        <LoadingIndicator>Cargando información del carrito...</LoadingIndicator>
      </CartContainer>
    );
  }

  return (
    <CartContainer>
      <Title>Mi Carrito</Title>
      
      {cartItems.length === 0 ? (
        <EmptyMessage>
          Tu carrito está vacío. Explora nuestra galería y agrega imágenes a tu carrito.
        </EmptyMessage>
      ) : (
        <CartContent>
          <CartItemsList>
            {cartPackages.map((pkg, index) => (
              <CartPackageItem key={index} cartPackage={pkg} />
            ))}
          </CartItemsList>
          <PromoCodeInput 
            onPromoCodeApplied={handlePromoCodeApplied}
            disabled={isProcessing}
          />
          {renderSummary()}
        </CartContent>
      )}
    </CartContainer>
  );
};

export default Cart;